import { useEffect, useRef, useState } from 'react';
import Select from 'react-select';
import makeAnimated from 'react-select/animated';
import './SelectAllCheckBox.css';
import { GrFormClose } from 'react-icons/gr';
import styled from 'styled-components';
import palette from '../../utils/colorPalette';
import { translateText } from '../../utils/translateText';
import i18n from '../../utils/i18n';
import { useTranslation } from 'react-i18next';
const animatedComponents = makeAnimated();

const CustomClearText = () => <>clear all</>;
const ClearIndicator = (props) => {
  const {
    children = <CustomClearText />,
    getStyles,
    innerProps: { ref, ...restInnerProps },
  } = props;
  const styleObj = getStyles('clearIndicator', props);
  styleObj['padding'] = '8px 0';

  return (
    <span
      {...restInnerProps}
      onMouseDown={(e) => {
        restInnerProps.onMouseDown(e);
        e.stopPropagation();
      }}
      ref={ref}
      style={styleObj}
    >
      <CloseHover size={24} />
    </span>
  );
};

const SelectAllCheckBox = ({
  selected,
  setSelected,
  isMulti,
  options,
  defaultInputValue,
  title,
  allSelectTitle,
  style,
  isClearable,
  isDisabled,
  menuIsOpen,
  ...props
}) => {
  const { t } = useTranslation();
  const language = i18n.language;
  const [translatedOptions, setTranslatedOptions] = useState(options);

  useEffect(() => {
    const updateOptions = options.map((option) => {
      return { 
        ...option, 
        label: language === 'ko' ? option.label : option.label_en || option.label 
      };
    });
    setTranslatedOptions(updateOptions);
  }, [language, options]);


  const styles = {
    menuPortal: (style) => ({ ...style, zIndex: 501 }),
    multiValueLabel: (style) => ({
      ...style,
      fontSize: '0.8rem',
      fontFamily: 'NotoSansKR-Regular',
    }),
    multiValue: (style) => ({
      ...style,
      backgroundColor: '#e5e5ed',
    }),
    Input: (style) => ({
      ...style,
      fontSize: '0.8rem',
    }),
    indicatorsContainer: (style) => ({
      ...style,
      height: '36px',
    }),
    valueContainer: (style) => ({
      ...style,
      overflow: 'auto',
      padding: '0',
      height: '36px',
    }),
    menuList: (base) => ({
      ...base,
      'overflow-x': 'hidden !important',
    }),
    ...style,
  };

  const valueRef = useRef(Array.isArray(selected) ? selected : []); // Always initialize as an array

  // useState와 useEffect는 조건문 바깥에서 선언
  const [value, setValue] = useState(
    !!defaultInputValue ? defaultInputValue : options[0]
  );
  const [dynamicOptions, setDynamicOptions] = useState(options);

  useEffect(() => {
    setValue(defaultInputValue);
  }, [defaultInputValue]);

  useEffect(() => {
    setDynamicOptions(options);
  }, [options]);

  // isMulti 조건에 상관없이 모든 Hook을 선언한 후 조건에 따라 로직 처리
  if (!!isMulti) {
    valueRef.current = Array.isArray(selected) ? selected : []; // Ensure it is always an array

    const selectAllOption = !!allSelectTitle
      ? {
          value: '<SELECT_ALL>',
          label: translateText({
            text: allSelectTitle,
            globalLanguage: language,
          }),
        }
      : {
          value: '<SELECT_ALL>',
          label: 'Select All',
        };

    const isSelectAllSelected = () =>
      valueRef.current.length === translatedOptions.length;

    const isOptionSelected = (option) =>
      valueRef.current.some(({ value }) => value === option.value) ||
      isSelectAllSelected();

    const getOptions = () => [selectAllOption, ...translatedOptions];

    const getValue = () =>
      isSelectAllSelected() ? [selectAllOption] : selected;

    const onChange = (newValue, actionMeta) => {
      const { action, option, removedValue } = actionMeta;

      if (
        action === 'select-option' &&
        option.value === selectAllOption.value
      ) {
        setSelected(translatedOptions, actionMeta);
      } else if (
        (action === 'deselect-option' &&
          option.value === selectAllOption.value) ||
        (action === 'remove-value' &&
          removedValue.value === selectAllOption.value)
      ) {
        setSelected([], actionMeta);
      } else if (
        action === 'deselect-option' &&
        newValue.length !== 0 &&
        newValue[0].value === '<SELECT_ALL>'
      ) {
        setSelected([option]);
      } else if (
        actionMeta.action === 'deselect-option' &&
        isSelectAllSelected()
      ) {
        setSelected(
          translatedOptions.filter(({ value }) => value !== option.value),
          actionMeta
        );
      } else {
        setSelected(newValue || [], actionMeta);
      }
    };

    return (
      <Select
        components={{ ClearIndicator }}
        isOptionSelected={isOptionSelected}
        options={getOptions()}
        value={getValue()}
        onChange={onChange}
        hideSelectedOptions={false}
        closeMenuOnSelect={false}
        className="basic-multi-select"
        defaultInputValue={defaultInputValue}
        isMulti
        placeholder={translateText({
          text: title,
          globalLanguage: language,
        })}
        isSearchable
        styles={styles}
        isDisabled={isDisabled}
        menuPosition="fixed"
      />
    );
  }

  return (
    <Select
      value={value}
      components={animatedComponents}
      options={dynamicOptions}
      hideSelectedOptions={false}
      className="basic-multi-select"
      defaultValue={value}
      closeMenuOnSelect={true}
      placeholder={translateText({
        text: title,
        globalLanguage: language,
      })}
      onChange={setSelected}
      isSearchable
      styles={styles}
      isDisabled={isDisabled}
      isClearable={!!isClearable}
      menuPosition="fixed"
    />
  );
};

export default SelectAllCheckBox;

const CloseHover = styled(GrFormClose)`
  &:hover {
    color: ${palette.buttonHover.change};
    transition: all ease-in 0.2s;
  }
`;
